import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { FileText, ExternalLink, GitCompareArrows, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface ClaimEstimate {
  id: string;
  source_format: string;
  carrier: string | null;
  claim_number: string | null;
  policy_number: string | null;
  insured_name: string | null;
  date_of_loss: string | null;
  type_of_loss: string | null;
  deductible: number | null;
  rcv_total: number | null;
  acv_total: number | null;
  depreciation_total: number | null;
  net_claim: number | null;
  sales_tax: number | null;
  recoverable_depreciation: number | null;
  op_overhead: number | null;
  op_profit: number | null;
  confidence: number | null;
  storage_bucket: string | null;
  storage_path: string | null;
  filename: string | null;
  created_at: string;
}

interface ClaimItem {
  id: string;
  line_no: number | null;
  section: string | null;
  description: string | null;
  quantity: number | null;
  unit: string | null;
  unit_price: number | null;
  rcv: number | null;
  depreciation: number | null;
  acv: number | null;
}

interface Props {
  claimEstimateId: string;
  onCompare?: (id: string) => void;
  onDeleted?: () => void;
}

const fmt = (n: number | null | undefined) =>
  n == null ? "—" : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

export const ClaimEstimateCard = ({ claimEstimateId, onCompare, onDeleted }: Props) => {
  const [claim, setClaim] = useState<ClaimEstimate | null>(null);
  const [items, setItems] = useState<ClaimItem[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const [{ data: c }, { data: i }] = await Promise.all([
      supabase.from("claim_estimates").select("*").eq("id", claimEstimateId).maybeSingle(),
      supabase.from("claim_estimate_items").select("*").eq("claim_estimate_id", claimEstimateId).order("line_no", { ascending: true }),
    ]);
    setClaim(c as any);
    setItems((i as any) || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, [claimEstimateId]);

  const openSource = async () => {
    if (!claim?.storage_path || !claim?.storage_bucket) {
      toast.error("No source file on record");
      return;
    }
    try {
      const { data, error } = await supabase.storage
        .from(claim.storage_bucket)
        .download(claim.storage_path);
      if (error || !data) throw error || new Error("No file");
      const blobUrl = URL.createObjectURL(data);
      window.open(blobUrl, "_blank");
      // Revoke after a minute so the tab has time to render
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    } catch (e: any) {
      console.error("openSource failed", e);
      toast.error(e?.message || "Could not open source PDF");
    }
  };

  const handleDelete = async () => {
    if (!confirm("Delete this parsed claim? Source PDF will be kept.")) return;
    const { error } = await supabase.from("claim_estimates").delete().eq("id", claimEstimateId);
    if (error) { toast.error("Delete failed"); return; }
    toast.success("Claim removed");
    onDeleted?.();
  };

  if (loading) return <Card><CardContent className="p-6 text-sm text-muted-foreground">Loading claim…</CardContent></Card>;
  if (!claim) return null;

  return (
    <Card>
      <CardHeader className="space-y-2">
        <div className="flex items-start justify-between gap-2">
          <div>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary" />
              {claim.carrier || "Adjuster Claim"}
              <Badge variant="secondary" className="uppercase">{claim.source_format}</Badge>
              {claim.confidence != null && (
                <Badge variant="outline">{Math.round(claim.confidence * 100)}% conf</Badge>
              )}
            </CardTitle>
            <div className="mt-1 text-xs text-muted-foreground">
              {claim.insured_name && <>Insured: {claim.insured_name} · </>}
              {claim.claim_number && <>Claim #: {claim.claim_number} · </>}
              {claim.date_of_loss && <>DoL: {claim.date_of_loss} · </>}
              {claim.type_of_loss && <>Loss: {claim.type_of_loss}</>}
            </div>
          </div>
          <div className="flex gap-2">
            {claim.storage_path && (
              <Button size="sm" variant="outline" onClick={openSource}>
                <ExternalLink className="mr-2 h-3 w-3" /> View Original PDF
              </Button>
            )}
            {onCompare && (
              <Button size="sm" onClick={() => onCompare(claim.id)}>
                <GitCompareArrows className="mr-2 h-3 w-3" /> Compare vs Estimate
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={handleDelete}>
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-8">
          <Stat label="RCV" value={fmt(claim.rcv_total)} />
          <Stat label="ACV" value={fmt(claim.acv_total)} />
          <Stat label="Depreciation" value={fmt(claim.depreciation_total)} />
          <Stat label="Recov. Depr." value={fmt(claim.recoverable_depreciation)} highlight />
          <Stat label="Sales Tax" value={fmt(claim.sales_tax)} />
          <Stat label="O&P (O/H)" value={fmt(claim.op_overhead)} />
          <Stat label="O&P (Profit)" value={fmt(claim.op_profit)} />
          <Stat label="Deductible" value={fmt(claim.deductible)} />
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Stat label="Net Claim (ACV − Deductible)" value={fmt(claim.net_claim)} highlight />
          <Stat label="Policy #" value={claim.policy_number || "—"} />
        </div>

        {items.length > 0 && items.length < 8 && (claim.rcv_total ?? 0) >= 5000 && (
          <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs">
            ⚠️ Only <strong>{items.length}</strong> line items extracted but RCV is {fmt(claim.rcv_total)}.
            This claim was parsed before the extraction fix. <strong>Delete it and re-import the PDF</strong> to recover the full line items.
          </div>
        )}

        <div className="rounded-md border">
          <div className="flex items-center justify-between border-b bg-muted/40 px-3 py-2 text-xs font-medium text-muted-foreground">
            <span>Line Items ({items.length})</span>
            {claim.storage_path && (
              <button onClick={openSource} className="text-primary hover:underline">
                Open original PDF →
              </button>
            )}
          </div>
          <div className="grid grid-cols-12 gap-2 border-b bg-muted/40 px-3 py-2 text-xs font-medium text-muted-foreground">
            <div className="col-span-1">#</div>
            <div className="col-span-4">Description</div>
            <div className="col-span-1 text-right">Qty</div>
            <div className="col-span-2 text-right">Unit $</div>
            <div className="col-span-1 text-right">RCV</div>
            <div className="col-span-1 text-right">Depr.</div>
            <div className="col-span-2 text-right">ACV</div>
          </div>
          {items.length === 0 ? (
            <div className="px-3 py-6 text-center text-sm text-muted-foreground">No line items extracted</div>
          ) : (
            <div className="max-h-96 overflow-y-auto">
              {items.map((it) => (
                <div key={it.id} className="grid grid-cols-12 gap-2 border-b px-3 py-2 text-sm last:border-b-0">
                  <div className="col-span-1 text-muted-foreground">{it.line_no ?? "—"}</div>
                  <div className="col-span-4">
                    <div className="truncate">{it.description || "—"}</div>
                    {it.section && <div className="text-xs text-muted-foreground">{it.section}</div>}
                  </div>
                  <div className="col-span-1 text-right tabular-nums">
                    {it.quantity ?? "—"} {it.unit || ""}
                  </div>
                  <div className="col-span-2 text-right tabular-nums">{fmt(it.unit_price)}</div>
                  <div className="col-span-1 text-right tabular-nums">{fmt(it.rcv)}</div>
                  <div className="col-span-1 text-right tabular-nums text-muted-foreground">{fmt(it.depreciation)}</div>
                  <div className="col-span-2 text-right tabular-nums font-medium">{fmt(it.acv)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const Stat = ({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) => (
  <div className={`rounded-md border p-2 ${highlight ? "border-primary/40 bg-primary/10" : "bg-muted/20"}`}>
    <div className="text-xs text-muted-foreground">{label}</div>
    <div className="text-sm font-semibold tabular-nums">{value}</div>
  </div>
);
